import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Button, LinearProgress, Alert, List, ListItem, ListItemText, Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import { getAdminLoans } from '../../services/loanService';
import { listPendingContractApprovals } from '../../services/contractsService';
import { AdminPageHeader, useAdminFeedback } from '../../components/admin/primitives';

export default function AdminStaffInbox() {
  const { showError, Feedback } = useAdminFeedback();
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState({
    loans: [],
    userApprovals: [],
    rtoAppeals: [],
    specialEvents: [],
    contractApprovals: [],
    challengeApprovals: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loansRes, usersRes, rtoRes, eventsRes, contractsRes, challengesRes] = await Promise.all([
        getAdminLoans({ status: 'pending', limit: 20 }).catch(() => ({ items: [] })),
        axiosInstance.get('/users/pending-approvals').catch(() => ({ data: [] })),
        axiosInstance.get('/admin/rto/challans', { params: { limit: 50 } }).catch(() => ({ data: { items: [] } })),
        axiosInstance.get('/special-events').catch(() => ({ data: [] })),
        listPendingContractApprovals().catch(() => []),
        axiosInstance.get('/challenges/pending-approvals').catch(() => ({ data: { challenges: [] } })),
      ]);

      const rtoItems = rtoRes?.data?.items || rtoRes?.data?.challans || rtoRes?.data || [];
      const appeals = (Array.isArray(rtoItems) ? rtoItems : []).filter((c) => c.appealStatus === 'pending');
      const events = Array.isArray(eventsRes?.data) ? eventsRes.data : (eventsRes?.data?.items || []);
      const specialWithPending = events.filter((e) => Number(e.pendingRequestCount || 0) > 0);
      const contractApprovals = Array.isArray(contractsRes) ? contractsRes : [];
      const challengeApprovals = Array.isArray(challengesRes?.data?.challenges)
        ? challengesRes.data.challenges
        : (Array.isArray(challengesRes?.data) ? challengesRes.data : []);

      setQueues({
        loans: loansRes?.items || [],
        userApprovals: Array.isArray(usersRes?.data) ? usersRes.data : (usersRes?.data?.items || []),
        rtoAppeals: appeals,
        specialEvents: specialWithPending,
        contractApprovals,
        challengeApprovals,
      });
    } catch (e) {
      showError(e?.response?.data?.message || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const total =
    queues.loans.length
    + queues.userApprovals.length
    + queues.rtoAppeals.length
    + queues.specialEvents.length
    + queues.contractApprovals.length
    + queues.challengeApprovals.length;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 3 }}>
      <AdminPageHeader
        title="Staff inbox"
        description="Pending loans, user approvals, RTO appeals, special-event allocations, and contract/challenge publish approvals."
        actions={<Button variant="outlined" onClick={load}>Refresh</Button>}
      />
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Alert severity="info" sx={{ mb: 2 }}>
        {total} open item{total === 1 ? '' : 's'} across queues.
      </Alert>

      <Stack spacing={2}>
        <QueueCard
          title="Contract approvals"
          count={queues.contractApprovals.length}
          to="/admin/contracts?tab=approvals"
          empty="No contracts awaiting publish approval."
          items={queues.contractApprovals.slice(0, 20).map((t) => ({
            id: t._id,
            primary: t.title || t.slug || t._id,
            secondary: t.startAt
              ? `Starts ${new Date(t.startAt).toLocaleString()}`
              : 'No start window set',
          }))}
        />
        <QueueCard
          title="Challenge approvals"
          count={queues.challengeApprovals.length}
          to="/admin/challenges?tab=approvals"
          empty="No challenges awaiting publish approval."
          items={queues.challengeApprovals.slice(0, 20).map((c) => ({
            id: c._id,
            primary: c.name || c._id,
            secondary: c.startDate
              ? `Starts ${new Date(c.startDate).toLocaleString()}`
              : 'No start date set',
          }))}
        />
        <QueueCard
          title="Pending loans"
          count={queues.loans.length}
          to="/admin/loans"
          empty="No pending loan requests."
          items={queues.loans.map((l) => ({
            id: l._id,
            primary: l.loanNumber || l._id,
            secondary: `${l.riderId?.name || l.riderId?.tmpIngameName || 'Rider'} · ${l.principal} tokens`,
          }))}
        />
        <QueueCard
          title="User approvals"
          count={queues.userApprovals.length}
          to="/admin/user-approvals"
          empty="No users awaiting approval."
          items={queues.userApprovals.slice(0, 20).map((u) => ({
            id: u._id,
            primary: u.username || u.email || u._id,
            secondary: u.role || 'rider',
          }))}
        />
        <QueueCard
          title="RTO appeals"
          count={queues.rtoAppeals.length}
          to="/admin/rto/challans"
          empty="No pending appeals."
          items={queues.rtoAppeals.slice(0, 20).map((c) => ({
            id: c._id,
            primary: c.challanNumber || c._id,
            secondary: c.appealReason || 'Appeal pending review',
          }))}
        />
        <QueueCard
          title="Special event allocations"
          count={queues.specialEvents.length}
          to="/admin/special-events"
          empty="No events with pending requests."
          items={queues.specialEvents.slice(0, 20).map((e) => ({
            id: e._id || e.truckersmpId,
            primary: e.name || e.title || e.truckersmpId,
            secondary: `${e.pendingRequestCount} pending request(s)`,
            to: `/admin/special-events/${e.truckersmpId}`,
          }))}
        />
      </Stack>
      <Feedback />
    </Box>
  );
}

function QueueCard({ title, count, to, empty, items }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{title}</Typography>
            <Chip size="small" label={count} color={count ? 'warning' : 'default'} />
          </Stack>
          <Button component={RouterLink} to={to} size="small">Open</Button>
        </Stack>
        {!items.length ? (
          <Typography variant="body2" color="text.secondary">{empty}</Typography>
        ) : (
          <List dense disablePadding>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  component={item.to ? RouterLink : 'li'}
                  to={item.to}
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <ListItemText primary={item.primary} secondary={item.secondary} />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
